import { useEffect, useRef } from "react";
import { useData } from "vike-react/useData";
import type { Data } from "./+data";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CategoryHeader from "@/components/category/CategoryHeader";
import CategoryRelated from "@/components/category/CategoryRelated";
import CategoryMostReviewed from "@/components/category/CategoryMostReviewed";
import CategoryFeed from "@/components/category/CategoryFeed";
import { BlogTrustLink } from "@/components/blog/BlogTrustLink";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";

// The category's experiences page (quick 261010-oyg): header with the honest numbers, related
// categories, the most-reviewed strip (tile = store, link = /stores filtered), then a search box
// and the home-feed cards over the category's reviews. The store directory itself lives on
// /stores?category=<slug>. SSR meta (+data/+title/+description/+Head) only changes its inputs.
export default function Page() {
  // `category` is never null here: +data.ts throws a Vike 404/500/redirect abort on any
  // load failure, so this component only renders for a resolved category.
  const { category, insights, feed } = useData<Data>();
  const { trackNavigation } = usePostHogTracking();
  const storesHref = `/stores?category=${encodeURIComponent(category.slug)}`;

  // category_viewed once per category (was fired by the Stores shell before the feed rework).
  const viewedFor = useRef<string | null>(null);
  useEffect(() => {
    if (viewedFor.current === category.id) return;
    viewedFor.current = category.id;
    trackNavigation.categoryViewed(category.title.ar, category.slug, { category_id: category.id });
  }, [category.id, category.slug, category.title.ar, trackNavigation]);

  return (
    <div className="catpage min-h-screen font-arabic">
      <Header />
      <main className="mx-auto max-w-[1280px] px-4 py-6 md:px-7 md:py-8">
        <CategoryHeader category={category} insights={insights} />
        <CategoryRelated insights={insights} categorySlug={category.slug} />
        <CategoryMostReviewed
          insights={insights}
          categoryTitle={category.title.ar}
          categorySlug={category.slug}
          storesHref={storesHref}
        />
        <div className="my-6"><BlogTrustLink surface="category_page" /></div>
        <CategoryFeed
          categorySlug={category.slug}
          categoryTitle={category.title.ar}
          initialPage={feed}
          storesHref={storesHref}
        />
      </main>
      <Footer variant="minimal" />
    </div>
  );
}
